<template>
  <div
    class="messages chat chat-history"
    :class="loadingContent ? 'loading large' : ''"
  >
    <div class="input-search">
      <form @submit.prevent="searchMessage()">
        <lila-input
          v-model="search"
          type="inputSearch"
          class="with-shadow"
          placeholder="Pesquisar na conversa"
          :btn-clear-search="activateClearSearchBtn"
          @custom-clear-search-input-event="clearSearch()"
        />
      </form>
    </div>

    <lila-message :alert-data="alert" />

    <div v-show="!loadingContent">
      <div class="ch-table-wrapper custom-scroll">
        <table class="ch-table">
          <thead>
            <tr>
              <th>Abertura do chamado</th>
              <th>Primeira mensagem</th>
              <th>Desfechos do encerramento</th>
              <th class="has-text-right">
                Chamado
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in data.chatList"
              :key="item.Id"
            >
              <td class="ch-date">
                <template v-if="firstTextMessage(item.Messages)">
                  {{ $Utilities.formatDateWithTime(firstTextMessage(item.Messages).DateAndTime, 'YYYY-MM-DDTHH:mm:ss', 'DD/MM/YYYY HH:mm:ss') }}
                </template>
                <template v-else>
                  {{ $Utilities.formatDateWithTime(item.Date, 'YYYY-MM-DDTHH:mm:ss', 'DD/MM/YYYY HH:mm:ss') }}
                </template>
              </td>

              <td class="ch-first-message">
                <template v-if="firstTextMessage(item.Messages)">
                  {{ firstTextMessage(item.Messages).Message }}
                </template>
                <template v-else>
                  Nenhuma mensagem foi encontrada.
                </template>
              </td>

              <td class="ch-outcomes">
                <div class="ch-outcomes-list">
                  <template v-if="outcomesWithText(item.Outcomes).length > 0">
                    <button
                      v-for="outcome in visibleOutcomes(item)"
                      :key="outcome.Id"
                      type="button"
                      class="ch-outcome-badge"
                      @click="openOutcomeModal(outcome)"
                    >
                      {{ outcomeDisplayName(outcome) }}
                    </button>
                    <button
                      v-if="outcomesWithText(item.Outcomes).length > 2"
                      type="button"
                      class="ch-outcome-more"
                      @click="toggleOutcomesExpanded(item.Id)"
                    >
                      {{ isOutcomesExpanded(item.Id) ? "ver menos" : `+${outcomesWithText(item.Outcomes).length - 2}` }}
                    </button>
                  </template>
                  <span
                    v-else
                    class="ch-outcome-empty"
                  >
                    Encerrado sem desfecho registrado
                  </span>
                </div>
              </td>

              <td class="has-text-right ch-view-more">
                <a
                  href="#"
                  @click.prevent="openChatHistoryModal(item.Messages[0] ? item.Messages[0].TicketId : item.Id)"
                >Ver mais</a>
              </td>
            </tr>
          </tbody>
        </table>

        <p
          v-if="!loadingContent && data.chatList.length === 0"
          class="ch-empty has-text-centered"
        >
          Nenhum chamado encerrado encontrado.
        </p>
      </div>

      <lila-pagination
        :pagination-config="pagination"
        @custom-change-page-event="changePage"
      />

      <div class="chat-history-modal default-modal">
        <div
          class="modal "
          :class="modalChatHistoryActive ? 'is-active' : ''"
        >
          <div class="modal-background" />

          <div class="modal-card ch-detail-card">
            <header class="modal-card-head ch-detail-head">
              <p class="modal-card-title ch-detail-title">
                <span>Encerrado em <strong>{{ data.selectedMessage.chatDate }}</strong></span>
                <span
                  v-if="data.selectedMessage.closedBy"
                  class="ch-detail-closed-by"
                >
                  Encerrado por <strong>{{ data.selectedMessage.closedBy }}</strong>
                </span>
              </p>
            </header>

            <section
              class="modal-card-body"
              :class="loadingChatHistory ? 'loading large' : ''"
            >
              <div
                ref="messagesListBox"
                class="messages-list custom-scroll"
              >
                <div v-if="data.selectedMessage?.messageList">
                  <lila-chat-message
                    v-for="(item, index) in data.selectedMessage.messageList"
                    :key="`chat-message-${index}`"
                    :message="item"
                    :last-date="index <= 0 ? undefined : data.selectedMessage.messageList[index - 1].DateAndTime"
                    :allow-edit="false"
                  />
                </div>
              </div>

              <div
                v-if="outcomesWithText(data.selectedMessage.outcomes).length > 0"
                class="ch-detail-outcomes"
              >
                <p class="ch-detail-outcomes-label">
                  Desfechos
                </p>
                <div class="ch-detail-outcomes-list">
                  <button
                    v-for="outcome in outcomesWithText(data.selectedMessage.outcomes)"
                    :key="outcome.Id"
                    type="button"
                    class="ch-outcome-badge"
                    @click="openOutcomeModal(outcome)"
                  >
                    {{ outcomeDisplayName(outcome) }}
                  </button>
                </div>
              </div>
            </section>

            <footer class="modal-card-foot">
              <lila-button
                type="submit"
                title="Fechar"
                class="button"
                :is-centralized="true"
                :loading="loadingChatHistory"
                @custom-click-event="closeChatHistoryModal()"
              />
            </footer>
          </div>
        </div>
      </div>

      <div class="chat-history-modal default-modal">
        <div
          class="modal"
          :class="outcomeModalActive ? 'is-active' : ''"
        >
          <div
            class="modal-background"
            @click="closeOutcomeModal()"
          />

          <div class="modal-card ch-outcome-card">
            <header class="modal-card-head">
              <p class="modal-card-title">
                Desfecho do chamado
              </p>
            </header>

            <section class="modal-card-body">
              <p>{{ selectedOutcome ? outcomeDisplayName(selectedOutcome) : '' }}</p>
            </section>

            <footer class="modal-card-foot">
              <lila-button
                type="submit"
                title="Fechar"
                class="button"
                :is-centralized="true"
                @custom-click-event="closeOutcomeModal()"
              />
            </footer>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Input from "@components/shared/fields/Input.vue";
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import Pagination from "@components/shared/pagination/Pagination.vue";
import LilaChatMessage from "@components/Chat/LilaChatMessage.vue";

// import { required, requiredIf } from '@vuelidate/validators';

export default {
  components: {
    "lila-input": Input,
    "lila-button": Button,
    "lila-message": Message,
    "lila-pagination": Pagination,
    LilaChatMessage
  },
  data(){
    return {
      data: {
        patientId: this.$route.params.id,
        ticketId: "",
        chatList: [],
        selectedMessage: {
          chatDate: "",
          closedBy: "",
          messageList: "",
          outcomes: []
        }
      },
      outcomeTypeNames: {},
      search: "",
      activateClearSearchBtn: false,
      modalChatHistoryActive: false,
      outcomeModalActive: false,
      selectedOutcome: null,
      expandedOutcomeRows: [],
      loadingContent: false,
      loadingChatHistory: false,
      alert: {
        message: "",
        type: "",
        show: false
      },
      pagination: {
        current: 0,
        totalPages: 0,
        perPage: 10
      }
    };
  },
  created(){
    this.loadOutcomeTypes();
    this.getChatHistoryList();
  },
  methods: {
    loadOutcomeTypes(){
      this.$axios
        .get(
          "/api/Outcome",
          {headers: {"Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}`}}
        )
        .then((res) => {
          this.outcomeTypeNames = (res.data || []).reduce((map, outcome) => {
            map[outcome.Id] = outcome.Name;
            return map;
          }, {});
        })
        .catch(error => {
          console.error("Erro ao carregar tipos de desfecho:", error);
        });
    },
    // Desfechos pré-definidos vêm com Description vazio (o backend só grava
    // texto livre quando o tipo é "Outros") — o nome vem da lista de tipos.
    outcomeDisplayName(outcome){
      return outcome.Description || this.outcomeTypeNames[outcome.OutcomeId] || "Desfecho";
    },
    getChatHistoryList(page = 1, searchParam = ""){
      this.alert.show = false;
      this.loadingContent = true;
      this.data.chatList = [];
      this.pagination.current = 0;
      this.pagination.totalPages = 0;

      if(this.activateClearSearchBtn) searchParam = this.search;

      this.$axios
        .get(
          `/api/chat/history/patient/${this.data.patientId}?page=${page}&limit=${this.pagination.perPage}${searchParam ? `&search=${searchParam}` : ""}`,
          {headers: {"Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}`}}
        )
        .then((res) => {
          this.data.chatList = (res.data.Data || []).map((item) => ({
            ...item,
            Outcomes: item.Outcomes || []
          }));

          this.pagination.current = res.data.Pagination.CurrentPage;
          this.pagination.totalPages = res.data.Pagination.TotalPage;
          this.pagination.perPage = res.data.Pagination.ItemPerPage;
        })
        .catch(error => {
          if(searchParam) this.closeChatHistoryModal();
          this.$Utilities.verifyToken(error.response.status);
          this.$Utilities.controlAlert(error.response.data, this.alert, error.response.status == 404 ? "warning" : "danger");
        })
        .finally(() => this.loadingContent = false);
    },
    getChatHistory(ticketId){
      this.alert.show = false;
      this.loadingChatHistory = true;

      this.$axios
        .get(
          `/api/chat/${ticketId}`,
          {headers: {"Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}`}}
        )
        .then((res) => {
          this.data.selectedMessage.chatDate = this.$Utilities.formatDateWithTime(res.data.EndingDateAndTime || res.data.StartingDateAndTime, "YYYY-MM-DDTHH:mm:ss", "DD/MM/YYYY - HH:mm:ss");
          this.data.selectedMessage.closedBy = res.data.ClosingProfessionalName || "";
          this.data.selectedMessage.messageList = res.data.Messages;
          this.data.selectedMessage.outcomes = res.data.Outcomes || [];
        })
        .catch(error => {
          this.closeChatHistoryModal();

          this.$Utilities.verifyToken(error.response.status);
          this.$Utilities.controlAlert(error.response.data, this.alert, "danger");
        })
        .finally(() => this.loadingChatHistory = false);
    },
    searchMessage(){
      this.activateClearSearchBtn = true;

      this.getChatHistoryList(1, this.search);
    },
    clearSearch(){
      this.search = "";
      this.activateClearSearchBtn = false;
      this.getChatHistoryList();
    },
    openChatHistoryModal(ticketId){
      this.getChatHistory(ticketId);

      this.modalChatHistoryActive = true;
    },
    closeChatHistoryModal(){
      this.data.selectedMessage.chatDate = "";
      this.data.selectedMessage.closedBy = "";
      this.data.selectedMessage.messageList = "";
      this.data.selectedMessage.outcomes = [];
      this.modalChatHistoryActive = false;
    },
    openOutcomeModal(outcome){
      this.selectedOutcome = outcome;
      this.outcomeModalActive = true;
    },
    closeOutcomeModal(){
      this.outcomeModalActive = false;
      this.selectedOutcome = null;
    },
    visibleOutcomes(item){
      const outcomes = this.outcomesWithText(item.Outcomes);
      return this.isOutcomesExpanded(item.Id) ? outcomes : outcomes.slice(0, 2);
    },
    isOutcomesExpanded(itemId){
      return this.expandedOutcomeRows.includes(itemId);
    },
    toggleOutcomesExpanded(itemId){
      this.expandedOutcomeRows = this.isOutcomesExpanded(itemId)
        ? this.expandedOutcomeRows.filter((id) => id !== itemId)
        : [...this.expandedOutcomeRows, itemId];
    },
    changePage(page){
      this.getChatHistoryList(page);
    },
    // Messages/Outcomes podem vir com itens sem texto (ex.: eventos de
    // chamada de vídeo misturados na lista) — ignora esses pra não mostrar
    // célula/badge em branco.
    firstTextMessage(messages){
      const withText = (messages || []).filter((m) => m.Message);
      if (withText.length === 0) return null;

      return withText.reduce((earliest, m) =>
        new Date(m.DateAndTime) < new Date(earliest.DateAndTime) ? m : earliest
      );
    },
    outcomesWithText(outcomes){
      return (outcomes || []).filter((o) => o.Description || o.OutcomeId);
    }
  }
};
</script>

<style>
    @import '~@assets/css/views/chat.css';
</style>

<style scoped>
.ch-table-wrapper {
  overflow-x: auto;
}
.ch-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.ch-table thead th {
  text-align: left;
  font-weight: 600;
  color: #4f3f82;
  padding: 10px 16px;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}
.ch-table tbody td {
  padding: 14px 16px;
  font-size: 14px;
  color: #3a3a3a;
  vertical-align: top;
  border-bottom: 1px solid #f0eef7;
}
.ch-table tbody tr:last-child td {
  border-bottom: none;
}
.ch-date {
  white-space: nowrap;
  color: #6b6680;
}
.ch-first-message {
  max-width: 360px;
}
.ch-outcomes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
}
.ch-outcome-badge {
  display: inline-block;
  vertical-align: top;
  box-sizing: border-box;
  height: 26px;
  border: none;
  background: #f0eef7;
  color: #4f3f82;
  font-size: 12px;
  line-height: 26px;
  font-weight: 600;
  padding: 0 12px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
}
.ch-outcome-badge:hover {
  background: #e5e0f3;
}
.ch-outcome-more {
  font-size: 11px;
  font-weight: 700;
  color: #8a83a0;
  background: transparent;
  border: 1px dashed #d9d5e6;
  border-radius: 999px;
  padding: 4px 8px;
  cursor: pointer;
}
.ch-outcome-more:hover {
  background: #f0eef7;
  color: #4f3f82;
}
.ch-outcome-empty {
  font-size: 13px;
  font-style: italic;
  color: #9a9a9a;
}
.ch-view-more a {
  color: #9a397d;
  font-weight: 600;
  white-space: nowrap;
}
.ch-empty {
  padding: 24px;
  color: #9a9a9a;
  font-size: 14px;
}
.ch-detail-card {
  width: 900px !important;
  max-width: 92vw !important;
  height: auto !important;
  max-height: calc(100vh - 120px) !important;
  border-radius: 12px !important;
  overflow: hidden;
}
.ch-detail-card .modal-card-head {
  padding: 22px 32px !important;
}
.ch-detail-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  text-align: left;
  font-size: 15px;
}
.ch-detail-card .modal-card-body {
  height: auto !important;
  max-height: calc(100vh - 260px);
  overflow-y: auto;
  padding: 28px 32px !important;
}
.ch-detail-card .messages-list {
  height: auto !important;
}
.ch-detail-closed-by {
  font-weight: 400;
  color: #6b6680;
  padding-left: 14px;
  border-left: 1px solid #d9d5e6;
}
.ch-detail-outcomes {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: flex-start;
  gap: 14px;
  border-top: 1px solid #eceaf3;
  margin-top: 24px;
  margin-left: 106px;
  padding: 20px 0 0;
}
.ch-detail-outcomes-label {
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 11px;
  line-height: 26px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8a83a0;
  margin: 0;
  flex: 0 0 auto;
}
.ch-detail-outcomes-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
}
.ch-detail-card .modal-card-foot {
  padding: 20px 32px !important;
}
.ch-outcome-card {
  width: 520px !important;
  max-width: 92vw !important;
  height: auto !important;
  max-height: none !important;
}
.ch-outcome-card .modal-card-body {
  padding: 24px;
  text-align: center;
  height: auto !important;
}
@media (max-width: 720px) {
  .ch-detail-outcomes {
    align-items: flex-start;
    grid-template-columns: 1fr;
    gap: 10px;
    margin-left: 0;
  }
}
</style>
